import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { parseQuickAdd, relativeDay } from '../myDayUtils';

const EXAMPLES = [
  'prep chutney tomorrow 7am p1 @kitchen ~45m',
  'count cash every weekday 9pm @finance',
  'call propane supplier friday p2',
  'post reel next monday @marketing #social',
];

/**
 * Todoist-style quick add: one line of plain English becomes a scheduled,
 * prioritised, categorised task. Whatever the parser understood is echoed back
 * as chips under the box, so nothing is captured silently.
 */
const QuickAdd = forwardRef(function QuickAdd({ today, onAdd, placeholder }, ref) {
  const [value, setValue] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }));

  const draft = value.trim() ? parseQuickAdd(value, today) : null;
  const canAdd = !!draft?.title;

  const submit = e => {
    e.preventDefault();
    if (!canAdd) return;
    onAdd(draft);
    setValue('');
  };

  return (
    <form className="md-quick" onSubmit={submit}>
      <div className="md-quick__box">
        <span className="md-quick__plus" aria-hidden="true">+</span>
        <input
          ref={inputRef}
          className="md-quick__input"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') { setValue(''); e.currentTarget.blur(); } }}
          placeholder={placeholder || 'Add an activity — try "order onions tomorrow 8am p1 @inventory ~20m"'}
          aria-label="Add an activity"
        />
        <button
          type="button"
          className="md-quick__help"
          onClick={() => setShowHelp(h => !h)}
          aria-expanded={showHelp}
          title="What can I type here?"
        >?</button>
        <button type="submit" className="md-btn md-btn--primary md-quick__go" disabled={!canAdd}>Add</button>
      </div>

      {draft && draft.matched.length > 0 && (
        <div className="md-quick__chips">
          <span className="md-quick__understood">Understood:</span>
          {draft.matched.map((m, i) => (
            <span key={`${m.kind}-${i}`} className={`md-chip md-chip--${m.kind}`}>{m.text}</span>
          ))}
          {draft.date && !draft.matched.some(m => m.kind === 'date') && (
            <span className="md-chip md-chip--date">{relativeDay(draft.date, today)}</span>
          )}
        </div>
      )}

      {showHelp && (
        <div className="md-quick__help-panel">
          <p className="md-quick__help-lead">Type it the way you'd say it. The parser picks up:</p>
          <ul className="md-quick__help-grid">
            <li><code>today</code> <code>tomorrow</code> <code>friday</code> <code>next monday</code> <code>in 3 days</code> <code>10/15</code> <code>oct 3</code></li>
            <li><code>7am</code> <code>9:30pm</code> <code>15:45</code> — blocks the time on your day</li>
            <li><code>p1</code>…<code>p4</code> priority · <code>@category</code> · <code>#tag</code></li>
            <li><code>~45m</code> <code>~1.5h</code> — how long you think it takes</li>
            <li><code>every day</code> <code>every weekday</code> <code>every monday</code> <code>every 14 days</code> — makes it a routine</li>
          </ul>
          <p className="md-quick__help-lead">Examples</p>
          <div className="md-quick__examples">
            {EXAMPLES.map(ex => (
              <button
                key={ex}
                type="button"
                className="md-chip md-chip--example"
                onClick={() => { setValue(ex); inputRef.current?.focus(); }}
              >{ex}</button>
            ))}
          </div>
        </div>
      )}
    </form>
  );
});

export default QuickAdd;
